import type { BookingStep, ProService } from '../types';
import type { CartItem } from '../contexts/CartContext';

export const PENDING_ANOTHER_SLOT_STORAGE_KEY = 'rintintin_pending_another_slot';

export type AnotherSlotPreserveSource = Pick<
  CartItem,
  | 'serviceId'
  | 'locationId'
  | 'employeeId'
  | 'service_address'
  | 'client_phone'
  | 'clientAnswer'
  | 'mileageData'
  | 'animalId'
>;

/** Étape créneau à rouvrir pour le même service (sans repasser par service / question / adresse). */
export function getAnotherSlotEntryStep(service: ProService, source: AnotherSlotPreserveSource): BookingStep {
  const isEvent = service.service_mode === 'event';
  const needsLocation =
    !service.is_home &&
    !service.is_online &&
    !!service.locations &&
    service.locations.length > 1;

  if (isEvent) {
    if (needsLocation && !source.locationId) return 'location';
    return 'session';
  }

  const needsAddressBeforeSlot =
    service.is_home && !!service.dynamic_travel_buffer_enabled;
  if (needsAddressBeforeSlot && !source.service_address) return 'address';

  return 'datetime';
}

export function cartItemToAnotherSlotSource(
  item: Omit<CartItem, 'cartItemId'>
): AnotherSlotPreserveSource {
  return {
    serviceId: item.serviceId,
    locationId: item.locationId,
    employeeId: item.employeeId,
    service_address: item.service_address,
    client_phone: item.client_phone,
    clientAnswer: item.clientAnswer,
    mileageData: item.mileageData,
    animalId: item.animalId,
  };
}

export function persistPendingAnotherSlot(item: Omit<CartItem, 'cartItemId'>): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PENDING_ANOTHER_SLOT_STORAGE_KEY, JSON.stringify(item));
  } catch {
    /* quota / private mode */
  }
}

export function readPendingAnotherSlot(): Omit<CartItem, 'cartItemId'> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_ANOTHER_SLOT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Omit<CartItem, 'cartItemId'>;
  } catch {
    return null;
  }
}

export function clearPendingAnotherSlot(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PENDING_ANOTHER_SLOT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
